import { useState } from 'react'
import {
  Box,
  Flex,
  Text,
  Badge,
  Table,
  IconButton,
  MenuRoot,
  MenuTrigger,
  MenuContent,
  MenuItem,
  MenuPositioner,
  Portal,
} from '@chakra-ui/react'
import {
  FileText,
  MoreVertical,
  Edit,
  Trash2,
  ExternalLink,
  CheckCircle,
  XCircle,
  CreditCard,
  AlertTriangle,
} from 'lucide-react'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import {
  useDeleteSupplierInvoice,
  useValidateSupplierInvoice,
  useCancelSupplierInvoice,
} from '../hooks'
import { accountingToaster as toaster } from '../pages/AccountingLayout'
import type { SupplierInvoiceWithRelations, SupplierInvoiceStatus, PaymentInvoiceStatus } from '../types'
import {
  SUPPLIER_INVOICE_STATUS_LABELS,
  SUPPLIER_INVOICE_STATUS_COLORS,
  PAYMENT_INVOICE_STATUS_LABELS,
  PAYMENT_INVOICE_STATUS_COLORS,
} from '../types'

interface SupplierInvoicesListProps {
  invoices?: SupplierInvoiceWithRelations[]
  searchTerm: string
  onEdit: (invoice: SupplierInvoiceWithRelations) => void
  onView: (invoice: SupplierInvoiceWithRelations) => void
  onCreatePayment: (invoice: SupplierInvoiceWithRelations) => void
  isLoading?: boolean
}

export function SupplierInvoicesList({
  invoices,
  searchTerm,
  onEdit,
  onView,
  onCreatePayment,
  isLoading,
}: SupplierInvoicesListProps) {
  const deleteInvoice = useDeleteSupplierInvoice()
  const validateInvoice = useValidateSupplierInvoice()
  const cancelInvoice = useCancelSupplierInvoice()
  const [invoiceToDelete, setInvoiceToDelete] = useState<SupplierInvoiceWithRelations | null>(null)
  const [invoiceToValidate, setInvoiceToValidate] = useState<SupplierInvoiceWithRelations | null>(null)
  const [invoiceToCancel, setInvoiceToCancel] = useState<SupplierInvoiceWithRelations | null>(null)

  const filtered = invoices?.filter(
    (i) =>
      i.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      i.internal_reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (i.suppliers?.name && i.suppliers.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (i.description && i.description.toLowerCase().includes(searchTerm.toLowerCase()))
  )

  const handleDelete = async () => {
    if (!invoiceToDelete) return

    try {
      await deleteInvoice.mutateAsync(invoiceToDelete.id)
      toaster.create({
        title: 'Succès',
        description: 'Facture supprimée',
        type: 'success',
      })
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Erreur lors de la suppression',
        type: 'error',
      })
    } finally {
      setInvoiceToDelete(null)
    }
  }

  const handleValidate = async () => {
    if (!invoiceToValidate) return

    try {
      await validateInvoice.mutateAsync(invoiceToValidate.id)
      toaster.create({
        title: 'Succès',
        description: 'Facture validée et écritures comptables générées',
        type: 'success',
      })
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Erreur lors de la validation',
        type: 'error',
      })
    } finally {
      setInvoiceToValidate(null)
    }
  }

  const handleCancel = async () => {
    if (!invoiceToCancel) return

    try {
      await cancelInvoice.mutateAsync(invoiceToCancel.id)
      toaster.create({
        title: 'Succès',
        description: 'Facture annulée',
        type: 'success',
      })
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : "Erreur lors de l'annulation",
        type: 'error',
      })
    } finally {
      setInvoiceToCancel(null)
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR')
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value)
  }

  const isOverdue = (invoice: SupplierInvoiceWithRelations) => {
    if (invoice.status !== 'VALIDATED' || invoice.payment_status === 'PAID') return false
    return new Date(invoice.due_date) < new Date()
  }

  if (isLoading) {
    return (
      <Box p={8} textAlign="center" bg="gray.50" borderRadius="lg">
        <Text color="gray.500">Chargement des factures...</Text>
      </Box>
    )
  }

  if (!filtered || filtered.length === 0) {
    return (
      <Box p={8} textAlign="center" bg="gray.50" borderRadius="lg">
        <FileText size={48} color="var(--chakra-colors-gray-300)" style={{ margin: '0 auto' }} />
        <Text mt={4} color="gray.500">
          {searchTerm ? 'Aucune facture trouvée' : 'Aucune facture enregistrée'}
        </Text>
        <Text fontSize="sm" color="gray.400">
          Cliquez sur "Nouvelle facture" pour en ajouter une
        </Text>
      </Box>
    )
  }

  return (
    <>
      <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
        <Table.Root size="sm">
          <Table.Header>
            <Table.Row bg="gray.50">
              <Table.ColumnHeader>N° Facture</Table.ColumnHeader>
              <Table.ColumnHeader>Fournisseur</Table.ColumnHeader>
              <Table.ColumnHeader>Date</Table.ColumnHeader>
              <Table.ColumnHeader>Échéance</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Montant TTC</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right">Reste dû</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="center">Statut</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="center">Paiement</Table.ColumnHeader>
              <Table.ColumnHeader w="80px" textAlign="right">
                Actions
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filtered.map((invoice) => (
              <Table.Row key={invoice.id} _hover={{ bg: 'gray.50' }} cursor="pointer">
                <Table.Cell onClick={() => onView(invoice)}>
                  <Text fontFamily="mono" fontWeight="medium">
                    {invoice.invoice_number}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {invoice.internal_reference}
                  </Text>
                </Table.Cell>
                <Table.Cell onClick={() => onView(invoice)}>
                  <Text fontWeight="medium">{invoice.suppliers?.name || '-'}</Text>
                  {invoice.contracts && (
                    <Text fontSize="xs" color="gray.500">
                      {invoice.contracts.label}
                    </Text>
                  )}
                </Table.Cell>
                <Table.Cell onClick={() => onView(invoice)}>
                  <Text fontSize="sm">{formatDate(invoice.invoice_date)}</Text>
                </Table.Cell>
                <Table.Cell onClick={() => onView(invoice)}>
                  <Flex align="center" gap={1}>
                    <Text fontSize="sm">{formatDate(invoice.due_date)}</Text>
                    {isOverdue(invoice) && (
                      <AlertTriangle size={14} color="var(--chakra-colors-red-500)" />
                    )}
                  </Flex>
                  {isOverdue(invoice) && (
                    <Text fontSize="xs" color="red.500">
                      En retard
                    </Text>
                  )}
                </Table.Cell>
                <Table.Cell textAlign="right" onClick={() => onView(invoice)}>
                  <Text fontFamily="mono" fontWeight="medium">
                    {formatCurrency(invoice.total_ttc)}
                  </Text>
                </Table.Cell>
                <Table.Cell textAlign="right" onClick={() => onView(invoice)}>
                  <Text
                    fontFamily="mono"
                    fontWeight="medium"
                    color={invoice.amount_remaining > 0 ? 'orange.600' : 'green.600'}
                  >
                    {formatCurrency(invoice.amount_remaining)}
                  </Text>
                </Table.Cell>
                <Table.Cell textAlign="center" onClick={() => onView(invoice)}>
                  <Badge
                    colorPalette={SUPPLIER_INVOICE_STATUS_COLORS[invoice.status as SupplierInvoiceStatus] || 'gray'}
                    size="sm"
                  >
                    {SUPPLIER_INVOICE_STATUS_LABELS[invoice.status as SupplierInvoiceStatus]}
                  </Badge>
                </Table.Cell>
                <Table.Cell textAlign="center" onClick={() => onView(invoice)}>
                  {invoice.status !== 'CANCELLED' && (
                    <Badge
                      colorPalette={PAYMENT_INVOICE_STATUS_COLORS[invoice.payment_status as PaymentInvoiceStatus] || 'gray'}
                      size="sm"
                    >
                      {PAYMENT_INVOICE_STATUS_LABELS[invoice.payment_status as PaymentInvoiceStatus]}
                    </Badge>
                  )}
                </Table.Cell>
                <Table.Cell textAlign="right">
                  <MenuRoot positioning={{ placement: 'bottom-end' }}>
                    <MenuTrigger asChild>
                      <IconButton variant="ghost" size="sm" aria-label="Actions">
                        <MoreVertical size={16} />
                      </IconButton>
                    </MenuTrigger>
                    <Portal>
                      <MenuPositioner>
                        <MenuContent minW="180px">
                          <MenuItem value="view" onClick={() => onView(invoice)}>
                            <ExternalLink size={16} />
                            <Text ml={2}>Voir le détail</Text>
                          </MenuItem>

                          {invoice.status === 'DRAFT' && (
                            <>
                              <MenuItem value="edit" onClick={() => onEdit(invoice)}>
                                <Edit size={16} />
                                <Text ml={2}>Modifier</Text>
                              </MenuItem>
                              <MenuItem
                                value="validate"
                                onClick={() => setInvoiceToValidate(invoice)}
                              >
                                <CheckCircle size={16} />
                                <Text ml={2}>Valider</Text>
                              </MenuItem>
                              <MenuItem
                                value="delete"
                                color="red.500"
                                onClick={() => setInvoiceToDelete(invoice)}
                              >
                                <Trash2 size={16} />
                                <Text ml={2}>Supprimer</Text>
                              </MenuItem>
                            </>
                          )}

                          {invoice.status === 'VALIDATED' &&
                            invoice.payment_status !== 'PAID' && (
                              <>
                                <MenuItem
                                  value="pay"
                                  onClick={() => onCreatePayment(invoice)}
                                >
                                  <CreditCard size={16} />
                                  <Text ml={2}>Créer un règlement</Text>
                                </MenuItem>
                                <MenuItem
                                  value="cancel"
                                  color="red.500"
                                  onClick={() => setInvoiceToCancel(invoice)}
                                >
                                  <XCircle size={16} />
                                  <Text ml={2}>Annuler</Text>
                                </MenuItem>
                              </>
                            )}
                        </MenuContent>
                      </MenuPositioner>
                    </Portal>
                  </MenuRoot>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Box>

      {/* Confirmation de suppression */}
      <ConfirmDialog
        isOpen={!!invoiceToDelete}
        onClose={() => setInvoiceToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer la facture"
        message={`Êtes-vous sûr de vouloir supprimer la facture "${invoiceToDelete?.invoice_number}" ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        colorPalette="red"
        icon={<Trash2 size={20} />}
        isLoading={deleteInvoice.isPending}
      />

      {/* Confirmation de validation */}
      <ConfirmDialog
        isOpen={!!invoiceToValidate}
        onClose={() => setInvoiceToValidate(null)}
        onConfirm={handleValidate}
        title="Valider la facture"
        message={`Êtes-vous sûr de vouloir valider la facture "${invoiceToValidate?.invoice_number}" ? Cette action générera les écritures comptables correspondantes.`}
        confirmLabel="Valider"
        colorPalette="green"
        icon={<CheckCircle size={20} />}
        isLoading={validateInvoice.isPending}
      />

      {/* Confirmation d'annulation */}
      <ConfirmDialog
        isOpen={!!invoiceToCancel}
        onClose={() => setInvoiceToCancel(null)}
        onConfirm={handleCancel}
        title="Annuler la facture"
        message={`Êtes-vous sûr de vouloir annuler la facture "${invoiceToCancel?.invoice_number}" ? Cette action ne peut pas être annulée.`}
        confirmLabel="Annuler la facture"
        colorPalette="red"
        icon={<XCircle size={20} />}
        isLoading={cancelInvoice.isPending}
      />
    </>
  )
}
